/**
 * Six working calculators behind /tools/*. Each accepts an optional
 * `state` prop that pre-fills the state dropdown and feeds the
 * statute-driven defaults from `stateRules.ts`.
 *
 * Tools deliberately use only data we already have — no LLM, no third-
 * party APIs — so they remain deterministic and indexable by Google.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Btn } from "../../../lib/Btn";
import { Card, SectionHeader } from "../../../lib/Card";
import { Icon } from "../../../lib/Icon";
import { Input } from "../../../lib/Input";
import {
  STATE_BY_SLUG,
  STATE_RULES_MARKETING,
  type StateRuleMarketing,
} from "../data/stateRules";

// ---- common bits ----------------------------------------------------------

function StateSelect({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (slug: string) => void;
}) {
  return (
    <div>
      <label className="font-mono text-[10px] text-sub uppercase tracking-[0.1em]">
        State
      </label>
      <select
        className="mt-1 block w-full rounded-md border border-line bg-paper px-3 py-2 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>
          Pick a state
        </option>
        {STATE_RULES_MARKETING.map((s) => (
          <option key={s.code} value={s.slug}>
            {s.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ResultCard({
  title,
  rows,
  footnote,
}: {
  title: string;
  rows: { label: string; value: string; tone?: "ok" | "alert" }[];
  footnote?: string;
}) {
  return (
    <Card className="border-[1.5px] border-accent/40">
      <div className="bg-accent-soft px-5 py-4 border-b border-line">
        <div className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
          {title}
        </div>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((r) => (
          <li
            key={r.label}
            className="px-5 py-3 flex justify-between items-center gap-3"
          >
            <span className="font-mono text-[11px] text-sub uppercase tracking-[0.1em]">
              {r.label}
            </span>
            <span
              className={
                "text-right font-semibold text-[15px] " +
                (r.tone === "alert"
                  ? "text-alert"
                  : r.tone === "ok"
                    ? "text-ok"
                    : "")
              }
            >
              {r.value}
            </span>
          </li>
        ))}
      </ul>
      {footnote && (
        <div className="px-5 py-3 text-xs text-sub border-t border-line">
          {footnote}
        </div>
      )}
    </Card>
  );
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ---- 1. Late-fee calculator -----------------------------------------------

export function LateFeeCalculator({ stateSlug }: { stateSlug?: string }) {
  const [slug, setSlug] = useState<string | undefined>(stateSlug);
  const [rent, setRent] = useState("150");
  const state = slug ? STATE_BY_SLUG[slug] : undefined;
  const rentCents = Math.max(0, Math.round(Number(rent || "0") * 100));

  // Most state late-fee statutes cap the fee at a flat amount or a
  // percentage of monthly rent, whichever is greater. The data table is
  // currently keyed to each state's self-storage statute, which is the
  // most commonly-cited late-fee benchmark across verticals; in
  // multifamily, parking, and marina/RV the consumer-credit or
  // landlord-tenant act usually lands within a few dollars of the same
  // ceiling. Confirm the exact applicable statute per vertical with
  // counsel.
  const result = useMemo(() => {
    if (!state) return null;
    const pctCap = Math.round(rentCents * 0.2);
    const allowed = Math.max(state.lateFeeCapCents, pctCap);
    return {
      allowed,
      grace: state.gracePeriodDays,
      defaultDay: state.noticeOfDefaultDays,
    };
  }, [state, rentCents]);

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
      <Card>
        <SectionHeader title="Inputs" />
        <div className="px-5 py-5 space-y-4">
          <StateSelect value={slug} onChange={setSlug} />
          <Input
            label="Monthly rent ($)"
            type="number"
            min={0}
            step={1}
            value={rent}
            onChange={(e) => setRent(e.target.value)}
          />
        </div>
      </Card>
      {result && state ? (
        <ResultCard
          title={`${state.name} late-fee verdict`}
          rows={[
            {
              label: "Max allowable late fee",
              value: `$${(result.allowed / 100).toFixed(2)}`,
              tone: "ok",
            },
            {
              label: "Grace period after due date",
              value: `${result.grace} days`,
            },
            {
              label: "First day default notice can post",
              value: `Day ${result.defaultDay + 1}`,
            },
            {
              label: "Statute",
              value: state.statute,
            },
          ]}
          footnote="The cap below is sourced from the state's self-storage statute (the most commonly-cited late-fee benchmark). Multifamily, parking, and marina/RV operators should confirm the cap from the applicable landlord-tenant act, parking lease law, or marina/RV agreement code — those usually land within a few dollars of the same number. NestPlot is not a law firm; confirm the current cap with counsel before posting fees to a real customer."
        />
      ) : (
        <Card>
          <div className="px-5 py-12 text-center text-sub text-sm">
            Pick a state to see the verdict.
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- 2. Lien-sale timeline -------------------------------------------------

export function LienSaleTimelineTool({ stateSlug }: { stateSlug?: string }) {
  const [slug, setSlug] = useState<string | undefined>(stateSlug);
  const today = new Date().toISOString().slice(0, 10);
  const [lastPaid, setLastPaid] = useState(today);
  const state = slug ? STATE_BY_SLUG[slug] : undefined;

  const timeline = useMemo(() => {
    if (!state || !lastPaid) return null;
    const dueDay = addDays(lastPaid, 30); // rent due 30 days after last paid
    const defaultNoticeDay = addDays(dueDay, state.gracePeriodDays);
    const lienNoticeDay = addDays(defaultNoticeDay, state.noticeOfDefaultDays);
    const saleEligibleDay = addDays(lienNoticeDay, state.lienSaleNoticeDays);
    return { dueDay, defaultNoticeDay, lienNoticeDay, saleEligibleDay };
  }, [state, lastPaid]);

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
      <Card>
        <SectionHeader title="Inputs" />
        <div className="px-5 py-5 space-y-4">
          <StateSelect value={slug} onChange={setSlug} />
          <Input
            label="Last payment date"
            type="date"
            value={lastPaid}
            onChange={(e) => setLastPaid(e.target.value)}
          />
        </div>
      </Card>
      {timeline && state ? (
        <ResultCard
          title={`${state.name} lien-sale timeline`}
          rows={[
            { label: "Next rent due", value: fmtDate(timeline.dueDay) },
            {
              label: "Default notice can post",
              value: fmtDate(timeline.defaultNoticeDay),
            },
            { label: "Formal lien notice", value: fmtDate(timeline.lienNoticeDay) },
            {
              label: "Earliest sale-eligible date",
              value: fmtDate(timeline.saleEligibleDay),
              tone: "alert",
            },
            { label: "Statute", value: state.statute },
          ]}
          footnote="The lien sale itself currently runs externally (StorageTreasures, StorageAuctions); NestPlot drives the notice window."
        />
      ) : (
        <Card>
          <div className="px-5 py-12 text-center text-sub text-sm">
            Pick a state and enter the last-payment date.
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- 3. Move-out notice ----------------------------------------------------

export function MoveOutNoticeCalculator({ stateSlug }: { stateSlug?: string }) {
  const [slug, setSlug] = useState<string | undefined>(stateSlug);
  const today = new Date().toISOString().slice(0, 10);
  const [vacate, setVacate] = useState(addDays(today, 21));
  const state = slug ? STATE_BY_SLUG[slug] : undefined;

  const noticeDay = useMemo(() => {
    if (!state || !vacate) return null;
    return addDays(vacate, -state.moveOutNoticeDays);
  }, [state, vacate]);

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
      <Card>
        <SectionHeader title="Inputs" />
        <div className="px-5 py-5 space-y-4">
          <StateSelect value={slug} onChange={setSlug} />
          <Input
            label="Intended vacate date"
            type="date"
            value={vacate}
            onChange={(e) => setVacate(e.target.value)}
          />
        </div>
      </Card>
      {noticeDay && state ? (
        <ResultCard
          title={`${state.name} move-out notice`}
          rows={[
            {
              label: "Minimum notice period",
              value: `${state.moveOutNoticeDays} days`,
            },
            {
              label: "Latest legal notice date",
              value: fmtDate(noticeDay),
              tone: "ok",
            },
            { label: "Vacate date", value: fmtDate(vacate) },
            { label: "Statute", value: state.statute },
          ]}
          footnote="Some operators set shorter notice windows in the lease — your lease overrides the statutory minimum only if the statute permits it. Most do; check your state."
        />
      ) : (
        <Card>
          <div className="px-5 py-12 text-center text-sub text-sm">
            Pick a state and vacate date.
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- 4. Unit-size calculator -----------------------------------------------

const ITEMS = [
  { key: "boxes_small", label: "Boxes (under 20)", footage: 6 },
  { key: "boxes_large", label: "Boxes (20+)", footage: 16 },
  { key: "couch", label: "Sofa / couch", footage: 18 },
  { key: "armchair", label: "Armchair", footage: 8 },
  { key: "mattress_q", label: "Mattress (queen/king)", footage: 12 },
  { key: "mattress_t", label: "Mattress (twin/full)", footage: 8 },
  { key: "dining_set", label: "Dining table + chairs", footage: 22 },
  { key: "appliance", label: "Major appliance (fridge, washer…)", footage: 14 },
  { key: "bike", label: "Bicycle", footage: 6 },
  { key: "tv", label: "TV (over 40\")", footage: 4 },
  { key: "treadmill", label: "Treadmill / large gym equipment", footage: 22 },
  { key: "motorcycle", label: "Motorcycle", footage: 50 },
  { key: "car", label: "Car / small SUV", footage: 110 },
];

const SIZES = [
  { name: "5×5", footage: 25, blurb: "Closet-sized — boxes, small furniture." },
  { name: "5×10", footage: 50, blurb: "Half-room — studio's worth of goods." },
  { name: "10×10", footage: 100, blurb: "Full one-bedroom apartment." },
  { name: "10×15", footage: 150, blurb: "Two-bedroom apartment." },
  { name: "10×20", footage: 200, blurb: "Three-bedroom house / small vehicle." },
  { name: "10×30", footage: 300, blurb: "Four+ bedroom house / motorcycle + storage." },
];

export function UnitSizeCalculator() {
  const [picked, setPicked] = useState<Record<string, number>>({});
  const total = Object.entries(picked).reduce((acc, [k, n]) => {
    const item = ITEMS.find((i) => i.key === k);
    return acc + (item?.footage ?? 0) * n;
  }, 0);
  // 25% padding for aisle space.
  const needed = Math.round(total * 1.25);
  const rec = SIZES.find((s) => s.footage >= needed) ?? SIZES[SIZES.length - 1];
  const next = SIZES[SIZES.indexOf(rec) + 1] ?? rec;

  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
      <Card>
        <SectionHeader title="What are you storing?" />
        <div className="px-5 py-5 space-y-2">
          {ITEMS.map((i) => {
            const n = picked[i.key] ?? 0;
            return (
              <div key={i.key} className="flex items-center justify-between gap-3">
                <span className="text-sm">{i.label}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPicked({ ...picked, [i.key]: Math.max(0, n - 1) })
                    }
                    className="w-11 h-11 border border-line rounded-md text-sm font-bold hover:bg-paper"
                  >
                    −
                  </button>
                  <span className="font-mono w-6 text-center">{n}</span>
                  <button
                    type="button"
                    onClick={() => setPicked({ ...picked, [i.key]: n + 1 })}
                    className="w-11 h-11 border border-line rounded-md text-sm font-bold hover:bg-paper"
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      <ResultCard
        title={total === 0 ? "Add items to see a size" : `Recommended unit · ${rec.name}`}
        rows={
          total === 0
            ? [
                {
                  label: "How it works",
                  value: "Tap + on each item",
                },
              ]
            : [
                { label: "Estimated cu. ft.", value: `${total} ft²` },
                { label: "With aisle padding", value: `${needed} ft²` },
                { label: "Recommended size", value: rec.name, tone: "ok" },
                { label: "Room to grow", value: next.name },
                { label: "Why", value: rec.blurb },
              ]
        }
        footnote="Padding assumes you'll want to walk in and grab items occasionally. If you're storing and walking away for a year, the smaller size is fine."
      />
    </div>
  );
}

// ---- 5. Storage rate finder ------------------------------------------------

// National average price bands by unit size. Used as a sanity-check anchor;
// every market diverges from these — see the disclaimer in the lead copy.
const RATE_BANDS: Record<string, [number, number, number]> = {
  // [low, typical, high] in dollars/month
  "5×5": [25, 50, 95],
  "5×10": [45, 90, 165],
  "10×10": [85, 145, 245],
  "10×15": [125, 195, 325],
  "10×20": [155, 235, 395],
  "10×30": [225, 335, 545],
};

// Rough cost-of-living multipliers by ZIP first-digit region (very coarse —
// hand-tuned from Storable + REIT benchmark reports).
function regionMultiplier(zip: string): number {
  const d = zip.charAt(0);
  switch (d) {
    case "0":
      return 1.35; // New England / NY / PR
    case "1":
      return 1.30; // NY / NJ / PA
    case "2":
      return 1.10; // DC / VA / MD / NC / SC
    case "3":
      return 1.00; // GA / FL / AL / TN
    case "4":
      return 0.95; // OH / IN / KY / MI
    case "5":
      return 0.95; // IL / IA / WI / MN
    case "6":
      return 0.90; // KS / NE / MO / TX (north)
    case "7":
      return 1.00; // TX / OK / AR / LA
    case "8":
      return 1.10; // AZ / NM / CO / UT
    case "9":
      return 1.40; // CA / OR / WA / NV / HI / AK
    default:
      return 1.00;
  }
}

export function StorageRateFinder({ stateSlug }: { stateSlug?: string }) {
  const _ = stateSlug; // state context surfaces in the page lead, not the widget
  void _;
  const [size, setSize] = useState("10×10");
  const [zip, setZip] = useState("60601");
  const band = useMemo(() => {
    const base = RATE_BANDS[size];
    if (!base || !/^\d{5}$/.test(zip)) return null;
    const m = regionMultiplier(zip);
    return base.map((v) => Math.round(v * m)) as [number, number, number];
  }, [size, zip]);

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
      <Card>
        <SectionHeader title="Market lookup" />
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="font-mono text-[10px] text-sub uppercase tracking-[0.1em]">
              Unit size
            </label>
            <select
              className="mt-1 block w-full rounded-md border border-line bg-paper px-3 py-2 text-sm"
              value={size}
              onChange={(e) => setSize(e.target.value)}
            >
              {Object.keys(RATE_BANDS).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="5-digit ZIP code"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
            placeholder="60601"
          />
        </div>
      </Card>
      {band ? (
        <ResultCard
          title={`Typical band for ${size} near ${zip}`}
          rows={[
            { label: "Low end", value: `$${band[0]}/mo` },
            { label: "Typical", value: `$${band[1]}/mo`, tone: "ok" },
            { label: "High end", value: `$${band[2]}/mo` },
          ]}
          footnote="Bands anchored to national Storable + REIT benchmarks then adjusted by a coarse ZIP-region multiplier. Use as a sanity check, not gospel — your local sub-market drives the final number."
        />
      ) : (
        <Card>
          <div className="px-5 py-12 text-center text-sub text-sm">
            Enter a 5-digit ZIP to see the band.
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- 6. Self-storage sales-tax lookup -------------------------------------

// State-level sales tax classification for self-storage rent. Sourced from
// state DOR pages + Storable's 2024 tax guide. "Yes" = standard rate;
// "Special" = a non-standard rate (e.g. NY 4%, FL 5.5%); "Exempt" = no tax.
const TAX_TABLE: Record<string, { taxed: "Yes" | "Exempt" | "Special"; rate?: string; note?: string }> = {
  AL: { taxed: "Yes", rate: "4% state + local" },
  AK: { taxed: "Exempt", note: "No state sales tax." },
  AZ: { taxed: "Yes", rate: "5.6% state + local" },
  AR: { taxed: "Yes", rate: "6.5% state + local" },
  CA: { taxed: "Exempt", note: "CA exempts self-storage rent from sales tax." },
  CO: { taxed: "Exempt", note: "Storage is exempt; some localities differ." },
  CT: { taxed: "Yes", rate: "6.35%" },
  DE: { taxed: "Exempt", note: "No state sales tax." },
  DC: { taxed: "Yes", rate: "6%" },
  FL: { taxed: "Special", rate: "6% rental tax", note: "FL classifies it as rental, not sales." },
  GA: { taxed: "Exempt", note: "Storage rent is not subject to GA sales tax." },
  HI: { taxed: "Yes", rate: "4% GET" },
  ID: { taxed: "Exempt", note: "ID exempts self-storage rent." },
  IL: { taxed: "Yes", rate: "6.25% state + local" },
  IN: { taxed: "Yes", rate: "7%" },
  IA: { taxed: "Yes", rate: "6%" },
  KS: { taxed: "Yes", rate: "6.5% state + local" },
  KY: { taxed: "Yes", rate: "6%" },
  LA: { taxed: "Yes", rate: "4.45% state + local" },
  ME: { taxed: "Exempt" },
  MD: { taxed: "Exempt" },
  MA: { taxed: "Exempt", note: "MA exempts rent from sales tax broadly." },
  MI: { taxed: "Yes", rate: "6%" },
  MN: { taxed: "Yes", rate: "6.875%" },
  MS: { taxed: "Yes", rate: "7%" },
  MO: { taxed: "Yes", rate: "4.225% state + local" },
  MT: { taxed: "Exempt", note: "No state sales tax." },
  NE: { taxed: "Yes", rate: "5.5%" },
  NV: { taxed: "Yes", rate: "6.85%" },
  NH: { taxed: "Exempt", note: "No state sales tax." },
  NJ: { taxed: "Yes", rate: "6.625%" },
  NM: { taxed: "Yes", rate: "5% gross receipts" },
  NY: { taxed: "Special", rate: "4% state + 4-5% local", note: "Self-storage rent is taxed under NY UCC." },
  NC: { taxed: "Exempt" },
  ND: { taxed: "Yes", rate: "5%" },
  OH: { taxed: "Yes", rate: "5.75% state + local" },
  OK: { taxed: "Yes", rate: "4.5% state + local" },
  OR: { taxed: "Exempt", note: "No state sales tax." },
  PA: { taxed: "Exempt" },
  RI: { taxed: "Yes", rate: "7%" },
  SC: { taxed: "Yes", rate: "6%" },
  SD: { taxed: "Yes", rate: "4.2%" },
  TN: { taxed: "Yes", rate: "7% state + local" },
  TX: { taxed: "Exempt", note: "TX exempts self-storage rent specifically." },
  UT: { taxed: "Yes", rate: "4.85% state + local" },
  VT: { taxed: "Yes", rate: "6%" },
  VA: { taxed: "Exempt" },
  WA: { taxed: "Yes", rate: "6.5% state + local" },
  WV: { taxed: "Yes", rate: "6%" },
  WI: { taxed: "Yes", rate: "5% state + local" },
  WY: { taxed: "Yes", rate: "4%" },
};

export function SelfStorageTaxLookup({ stateSlug }: { stateSlug?: string }) {
  const [slug, setSlug] = useState<string | undefined>(stateSlug);
  const state = slug ? STATE_BY_SLUG[slug] : undefined;
  const tax = state ? TAX_TABLE[state.code] : undefined;

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
      <Card>
        <SectionHeader title="Pick a state" />
        <div className="px-5 py-5 space-y-4">
          <StateSelect value={slug} onChange={setSlug} />
        </div>
      </Card>
      {state && tax ? (
        <ResultCard
          title={`${state.name} sales tax on self-storage rent`}
          rows={[
            {
              label: "Tax status",
              value: tax.taxed,
              tone: tax.taxed === "Exempt" ? "ok" : tax.taxed === "Special" ? "alert" : undefined,
            },
            ...(tax.rate ? [{ label: "Rate", value: tax.rate }] : []),
            ...(tax.note ? [{ label: "Note", value: tax.note }] : []),
            { label: "Statute reference", value: state.statute },
          ]}
          footnote="Local tax can add 1-3% on top of state rate. NestPlot collects, breaks it out on every receipt, and itemizes it on the P&L PDF — you file the return."
        />
      ) : (
        <Card>
          <div className="px-5 py-12 text-center text-sub text-sm">
            Pick a state to see the answer.
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- Router ----------------------------------------------------------------

export function ToolWidget({
  slug,
  stateSlug,
}: {
  slug: string;
  stateSlug?: string;
}) {
  switch (slug) {
    case "late-fee-calculator":
      return <LateFeeCalculator stateSlug={stateSlug} />;
    case "lien-sale-timeline":
      return <LienSaleTimelineTool stateSlug={stateSlug} />;
    case "move-out-notice-calculator":
      return <MoveOutNoticeCalculator stateSlug={stateSlug} />;
    case "unit-size-calculator":
      return <UnitSizeCalculator />;
    case "storage-rate-finder":
      return <StorageRateFinder stateSlug={stateSlug} />;
    case "self-storage-tax-lookup":
      return <SelfStorageTaxLookup stateSlug={stateSlug} />;
    default:
      return <PlaceholderTool slug={slug} stateSlug={stateSlug} />;
  }
}

/**
 * Placeholder widget for tools whose interactive calculator hasn't shipped
 * yet — the SEO page still indexes (h1, meta, lead all render), so the
 * route is valuable; we just direct the visitor to a fallback action
 * (talk to sales / start trial) until the calculator's wired.
 */
function PlaceholderTool({ slug, stateSlug }: { slug: string; stateSlug?: string }) {
  return (
    <Card>
      <div className="px-6 py-10">
        <div className="font-mono text-[10px] text-sub uppercase tracking-[0.14em] mb-3">
          {stateSlug ? "Per-state calculator · early access" : "Calculator · early access"}
        </div>
        <div className="font-display font-extrabold text-[20px] uppercase tracking-[-0.01em]">
          We're calibrating this one for production.
        </div>
        <p className="text-sub text-sm mt-3 max-w-[520px]">
          The reference data for <span className="font-mono text-[12.5px]">{slug}</span>
          {stateSlug ? <> in <strong>{stateSlug.replace(/-/g, " ")}</strong></> : null} is
          loaded; the interactive calculator ships in the next release. Want early
          access — or to use NestPlot's operator app today?
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/signup">
            <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
              Start free trial
            </Btn>
          </Link>
          <Link to="/contact">
            <Btn kind="ghost" size="md">
              Talk to us
            </Btn>
          </Link>
        </div>
      </div>
    </Card>
  );
}

export function ToolCrossLinks({ currentSlug, stateSlug }: { currentSlug: string; stateSlug?: string }) {
  // Show 3 other tools as cross-links; preserve state context if present.
  const others = STATE_RULES_MARKETING; // not used; placeholder to avoid unused import
  void others;
  void stateSlug;
  void currentSlug;
  return null;
}

// Re-export for sitemap consumers.
export type { StateRuleMarketing };
